/**
 * One site's reporter link, latest report and history.
 *
 * Polled once a minute while the tab is visible and a token exists, so the
 * first report shows up on its own after the token is pasted into the plugin.
 * A late answer for an earlier read never overwrites a newer one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getReporter, type ReporterDetailResponse } from "../../../../lib/monitoringReporter";
import { MONITOR_POLL_MS, useVisiblePoll } from "../useMonitors";

export function useReporter(monitorId: string) {
  const [data, setData] = useState<ReporterDetailResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    try {
      const res = await getReporter(monitorId);
      if (!alive.current || rid !== requestId.current) return;
      setData(res);
      setFailed(false);
    } catch {
      if (!alive.current || rid !== requestId.current) return;
      setFailed(true);
    }
  }, [monitorId]);

  useEffect(() => {
    setData(null);
    setFailed(false);
    void load();
  }, [load]);

  useVisiblePoll(Boolean(data?.link), MONITOR_POLL_MS, () => void load());

  return { data, failed, reload: load };
}
